/** @module Auth0LinkingPageHelper */
/* eslint-disable import/extensions */
import Auth0Constants from '../../utils/auth0-constants.mjs';
import { resolveSsasBaseUrl } from '../../utils/ssas-urls.mjs';
import Auth0Tenant from '../../utils/auth0-tenant-utils.mjs';
import Auth0Localizer from '../../utils/auth0-localization-helper.mjs';
import Auth0ManagementApiBrowser from '../../utils/auth0-management-api-browser.mjs';
import Auth0DynamicScriptLoader from '../../utils/auth0-dynamic-script-loader.mjs';
import Auth0SupportedIdps from '../../utils/auth0-supported-idps.mjs';
import Auth0UtilsBrowser from '../../utils/auth0-utils-browser.mjs';
import Auth0CookieHelper from '../../utils/auth0-cookie-helper.mjs';
import Auth0Cdn from '../utils/auth0-cdn-resource-resolver.mst.mjs';
import Auth0WhiteLabelAssetHelper from '../utils/auth0-white-label-helper.mst.mjs';
import AccountLinkingHtml from '../../../public/ssolink.html.mst';
import { Auth0PageBase } from './auth0-page-template.mst.mjs';

class Auth0LinkingPageHelper extends Auth0PageBase {
  /**
   * @constructor
   * @description This package is intended for use in SSO account linking flow. It is designed to render the linking process confirmation and then support popup-based login to a ShipStation account and, finally, redirect back to the calling app in order to complete the login to said app (e.g. ShipStation V3). Using it requires on that it be loaded, a new object of type `Auth0LinkingPageHelper` be instantiated and that the `start()` method os called. Because it is an ES6 module, you'll need to use the `import` directive in a `script` block of type `module`. Note that each tenant hosts its own `auth0-sso-link-page-helper` package and, as such, the `import` directive must be dynamic. Note that all parameters are required by may be passed into the constructor directly or may optionally exist as a query string param for the current page. The passed in `options` object has precedence.
   * @param {string} options Linking options object (required: options may alternatively be provided via current page query string args)
   * @param {string} options.domain The shipstation.com signin domain for the tenant (e.g. signin.shipstation.com, signin-stage.shipstation.com, etc)
   * @param {string} options.tenant The shipstation.com tenant name (e.g. shipstation, shipstation-stage, etc)
   * @param {string} options.clientId The client ID of the Auth0 application starting the auth flow (e.g. mvUDKj8esZ2DFqLjEKzmta5rJEhXHbCP)
   * @param {string} options.connection The system of record for authorizing a user FROM SHIPSTATION. Should almost always be "Central-Connection"
   * @param {string} options.given_name According to the currently authorized SSO IdP, the user's first name
   * @param {string} options.name According to the currently authorized SSO IdP, the user's full name
   * @param {string} options.email According to the currently authorized SSO IdP, the user's email address
   * @param {string} options.picture According to the currently authorized SSO IdP, the user's avatar icon
   * @param {string} options.user_id According to the currently authorized SSO IdP, the user's IdP-based user ID. This ID must be prefixed by the IdPs ID and then the '|' character (e.g. google-oauth2|1234567890)
   * @param {string} options.idp_id According to the currently authorized SSO Identity Provider, the ID of the IdP name (e.g. google-oauth2)
   * @param {string} options.appLoginHandlerUrl The consuming application login URL, Also, if the user cancels the linking operation, specifies where user should be sent after IdP logout
   * @param {string} options.redirectUrl If the user successfully completes the linking operation, where user should be sent in order to complete the login
   * @param {string} options.appName The name of the hosting application (i.e. ShipStation, ShipEngine). For white labeling purposes
   * @param {string} [options.message] An optional notification message to show the user (above the linking data). Ideally this will be a localization key, but, if not, the text here will display as is
   * @param {object} [options.messageReplacementArgs] A standard set of replacement properties if the localization key defined in options.message accepts any.
   * @param {object} [options.whiteLabelOptions] Optionally set various functional and style attributes for linking
   * @param {object} [options.captchaOptions] Optionally enable / disable captcha widget and captcha type for the SS login popup
   * @param {boolean} [options.captchaOptions.disableCaptcha=false] This will disable the captcha widget and has no bearing on if the auth server expects a captcha token
   * @param {string} [options.captchaOptions.captchaType=turnstile] This will select either Google or Cloudflare captcha providers
   * @param {string} [options.captchaOptions.auth0LoginConfigToken] @deprecated SSAS now generates signed tokens server-side based on LD flags. Client-provided tokens are only used for backwards compatibility.
   * @example
   * <script type="text/javascript" src="https://cdn.shipstation.com/auth0/shipstation-stage/auth0.ssoLinking.min.js?cb=${cb}"></script>
   * <script type="text/javascript">
   *  const linkingPageHelper = new auth0Helper.ssoLinking.Auth0LinkingPageHelper({ options });
   * </script>
   */
  constructor(options) {
    super(options);
    console.log('New Auth0LinkingPageHelper');

    const tenant = options?.tenant;
    const idpId = options?.idp_id;
    const appLoginHandlerUrl = options?.appLoginHandlerUrl;
    this._idpInfo = new Auth0SupportedIdps({
      tenantOrDomain: tenant,
      appLoginHandlerUrl,
    }).getIdpById(idpId);

    console.log(`_idpInfo => ${typeof this._idpInfo}`);
    console.log(`[Auth0LinkingPageHelper] Constructor received options.redirectUrl: ${options?.redirectUrl}`);
    console.log(`[Auth0LinkingPageHelper] Constructor received options.appLoginHandlerUrl: ${options?.appLoginHandlerUrl}`);
    this.linkingOptions = {
      moduleName: 'Auth0LinkingPageHelper',
      domain: options?.domain,
      tenant,
      clientId: options?.clientId,
      connection: options?.connection || 'Central-Connection',
      given_name: options?.given_name,
      name: options?.name,
      email: options?.email,
      picture: options?.picture,
      user_id: options?.user_id,
      idp_name: this._idpInfo.name,
      idp_icon: this._idpInfo.logoUrl,
      idp_id: idpId,
      appLoginHandlerUrl,
      redirectUrl: options?.redirectUrl,
      appName: options?.appName || 'ShipStation',
      tokenStorageConfig: options?.tokenStorageConfig,
    };
    console.log(`[Auth0LinkingPageHelper] linkingOptions.redirectUrl: ${this.linkingOptions.redirectUrl}`);
    console.log(`[Auth0LinkingPageHelper] linkingOptions.tokenStorageConfig: ${JSON.stringify(this.linkingOptions.tokenStorageConfig ?? null)}`);

    this.tenant = new Auth0Tenant(this.linkingOptions.tenant);
    this.scriptLoader = new Auth0DynamicScriptLoader(this.linkingOptions.tenant);
    console.log(`Auth0LinkingPageHelper message => ${options?.message}`);
    this._notificationMessage = options?.message;
    this._notificationMessageReplacementArgs = options?.messageReplacementArgs;
    this._captchaOptions = options?.captchaOptions;
    this._whiteLabelOptions = options?.whiteLabelOptions;

    this._pageStyles = [
      `${Auth0Cdn.AUTH0_STYLES_URL}/auth0-linking-confirmation-styles.css?cb=71c5d67`,
      `${Auth0Cdn.AUTH0_STYLES_URL}/auth0-idp-button-styles.css?cb=71c5d67`,
    ];

    this._pageScripts = [
      Auth0Constants.AUTH0_SPAJS_URL,
    ];
  }

  _enableLinkingConfirmButton = (enabled, buttonTextResourceId = null) => {
    const btn = document.getElementById('connect-to-idp-button');
    btn.disabled = !enabled;
    let resourceId = buttonTextResourceId;
    if (!resourceId) {
      resourceId = enabled ? 'connect-to-idp-button' : 'connect-to-idp-button-disabled';
    }
    Auth0Localizer.setElementLocalizedText({
      id: 'connect-to-idp-button',
      resourceIdOverride: resourceId,
    }, { appName: this.linkingOptions.appName });
    if (enabled) {
      btn.classList.remove('disabled-button');
    } else {
      btn.classList.add('disabled-button');
    }
  };

  _getIdpAuth0Client = async () => {
    if (!this.auth0IdPClient) {
      const a0Options = {
        domain: this.linkingOptions.domain,
        clientId: this.linkingOptions.clientId,
        authorizationParams: {
          connection: this.linkingOptions.idp_id,
          scope: 'openid email profile read:current_user',
          // scope: 'openid email profile read:current_user update:current_user_identities',
        },
        cacheLocation: 'localstorage',
      };
      console.log(`_getIdpAuth0Client => ${JSON.stringify(a0Options)}`);
      this.auth0IdPClient = await window.auth0.createAuth0Client(a0Options);
    }

    return this.auth0IdPClient;
  };

  _getSsAuth0Client = async () => {
    if (!this.auth0SsClient) {
      console.log('create new ss client...');
      const a0Options = {
        domain: this.linkingOptions.domain,
        clientId: this.linkingOptions.clientId,
        authorizationParams: {
          connection: this.linkingOptions.connection,
        },
        cacheLocation: 'localstorage',
      };
      this.auth0SsClient = await window.auth0.createAuth0Client(a0Options);
    }

    return this.auth0SsClient;
  };

  /**
   * Cache SSO user's credentials in sessionStorage before redirecting to SS login.
   * These will be retrieved after SS auth to complete the linking via SSAS proxy.
   * @param {object} ssoCredentials - SSO user credentials to cache
   * @param {string} ssoCredentials.ssoUserJwt - SSO user's ID token JWT (__raw from getIdTokenClaims)
   * @param {string} ssoCredentials.ssoIdToken - SSO user's ID token (same as JWT for linking)
   * @param {string} ssoCredentials.ssoUserId - SSO user's Auth0 user ID (sub claim)
   */
  _cacheSsoCredentialsForRedirectFlow = (ssoCredentials) => {
    console.log('_cacheSsoCredentialsForRedirectFlow: caching SSO credentials in sessionStorage');
    sessionStorage.setItem(Auth0Constants.AUTH0_LINKING_CACHED_SSO_JWT, ssoCredentials.ssoUserJwt);
    sessionStorage.setItem(Auth0Constants.AUTH0_LINKING_CACHED_SSO_ID_TOKEN, ssoCredentials.ssoIdToken);
    sessionStorage.setItem(Auth0Constants.AUTH0_LINKING_CACHED_SSO_USER_ID, ssoCredentials.ssoUserId);

    // Cache metadata needed for post-linking redirect
    const metadata = {
      idpId: this.linkingOptions.idp_id,
      email: this.linkingOptions.email,
      appLoginHandlerUrl: this.linkingOptions.appLoginHandlerUrl,
      redirectUrl: this.linkingOptions.redirectUrl,
      tokenStorageConfig: this.linkingOptions.tokenStorageConfig,
    };
    sessionStorage.setItem(Auth0Constants.AUTH0_LINKING_CACHED_METADATA, JSON.stringify(metadata));
    console.log('_cacheSsoCredentialsForRedirectFlow: cached SSO credentials and metadata:', {
      ssoUserId: ssoCredentials.ssoUserId,
      hasJwt: !!ssoCredentials.ssoUserJwt,
      metadata: { ...metadata, tokenStorageConfig: metadata.tokenStorageConfig ?? null },
    });
  };

  /**
   * Retrieve cached SSO credentials from sessionStorage.
   * Used by post-login handler to complete linking after SS auth redirect.
   * @returns {object|null} Cached SSO credentials or null if not found
   */
  static getCachedSsoCredentials = () => {
    const ssoUserJwt = sessionStorage.getItem(Auth0Constants.AUTH0_LINKING_CACHED_SSO_JWT);
    const ssoIdToken = sessionStorage.getItem(Auth0Constants.AUTH0_LINKING_CACHED_SSO_ID_TOKEN);
    const ssoUserId = sessionStorage.getItem(Auth0Constants.AUTH0_LINKING_CACHED_SSO_USER_ID);
    const metadataStr = sessionStorage.getItem(Auth0Constants.AUTH0_LINKING_CACHED_METADATA);

    if (!ssoUserJwt || !ssoUserId) {
      console.log('getCachedSsoCredentials: no cached SSO credentials found');
      return null;
    }

    const metadata = metadataStr ? JSON.parse(metadataStr) : {};
    console.log('getCachedSsoCredentials: retrieved cached SSO credentials');

    return {
      ssoUserJwt,
      ssoIdToken,
      ssoUserId,
      ...metadata,
    };
  };

  /**
   * Clear cached SSO credentials from sessionStorage.
   * Called after linking is complete (success or failure).
   */
  static clearCachedSsoCredentials = () => {
    console.log('clearCachedSsoCredentials: removing cached SSO credentials');
    sessionStorage.removeItem(Auth0Constants.AUTH0_LINKING_CACHED_SSO_JWT);
    sessionStorage.removeItem(Auth0Constants.AUTH0_LINKING_CACHED_SSO_ID_TOKEN);
    sessionStorage.removeItem(Auth0Constants.AUTH0_LINKING_CACHED_SSO_USER_ID);
    sessionStorage.removeItem(Auth0Constants.AUTH0_LINKING_CACHED_METADATA);
  };

  /**
   * Redirect-based login to ShipStation account for account linking.
   * Replaces popup-based flow that's blocked by Safari/iOS WebViews.
   *
   * Flow:
   * 1. Cache SSO user's JWT in sessionStorage
   * 2. Redirect to SS auth with appState flag
   * 3. After SS auth, post-login handler detects flag and completes linking via SSAS
   */
  _loginToShipStationWithRedirect = async () => {
    this._writeLoginToShipStationMessageCookie();

    // Get SSO user's claims (JWT needed for linking)
    const secondaryClaims = await this._getSecondaryUserClaims();
    if (!secondaryClaims) {
      console.log('_loginToShipStationWithRedirect: unable to get SSO user claims');
      throw new Error('Unable to get SSO user claims');
    }

    const {
      __raw: ssoUserJwt,
      sub: ssoUserId,
      email: ssoEmail,
      email_verified: emailVerified,
    } = secondaryClaims;

    // Validate email is verified before allowing linking
    if (Auth0UtilsBrowser.hasProp(secondaryClaims, 'email_verified') && !emailVerified) {
      this._displayError('error-idp-email-unverified', { emailAddress: ssoEmail || 'unknown', idpName: this._idpInfo.name });
      throw new Error('SSO email not verified');
    }

    // Cache SSO credentials in sessionStorage for retrieval after SS auth
    this._cacheSsoCredentialsForRedirectFlow({
      ssoUserJwt,
      ssoIdToken: ssoUserJwt, // ID token is same as JWT for linking
      ssoUserId,
    });

    // Redirect to SS auth with appState flag to signal linking completion
    const a0 = await this._getSsAuth0Client();

    // Build redirect_uri with required query params for tenant resolution
    // Auth0 allowed callback URLs use wildcards (https://*.shipstation.com/loginauth)
    // so we can include query params as long as the base URL matches
    const baseUrl = `${window.location.origin}/loginauth`;
    const redirectParams = new URLSearchParams();

    // Include clientId for tenant resolution on callback
    if (this.linkingOptions.clientId) {
      redirectParams.set('targetClientId', this.linkingOptions.clientId);
    }

    // Forward authOptionsToken so SSAS injects white label on the callback /loginauth page.
    // The token may not be in the QS (SSAS consumed it server-side to render this page),
    // so fall back to window._ssasAuthOptionsToken which auth0-login.ejs always sets when WL is active.
    const authOptionsToken = Auth0UtilsBrowser.parseQueryString().get('authOptionsToken') || window._ssasAuthOptionsToken;
    if (authOptionsToken) {
      redirectParams.set('authOptionsToken', authOptionsToken);
    }

    const redirectUri = redirectParams.toString()
      ? `${baseUrl}?${redirectParams.toString()}`
      : baseUrl;

    const loginOptions = {
      authorizationParams: {
        connection: 'Central-Connection',
        max_age: 0,
        prompt: 'login', // Force fresh login, ignore existing SSO session
        scope: 'openid email profile',
        redirect_uri: redirectUri,
        enableSso: false,
        // Force username/password auth without progressive auth or SSO buttons
        progressiveAuth: 'false',
        // Include white label so Auth0 Universal Login page uses correct branding during linking
        ...(this._whiteLabelOptions && Object.keys(this._whiteLabelOptions).length > 0 && {
          whiteLabelJson: JSON.stringify(this._whiteLabelOptions),
          ...this._whiteLabelOptions,
        }),
      },
      appState: {
        targetUrl: this.linkingOptions.redirectUrl,
        // redirectOverride ensures SSHome bypasses hostname rewriting and redirects directly
        // to the originating page (e.g. /settings/signin-security) after linking completes.
        redirectOverride: this.linkingOptions.redirectUrl,
        // Signal to post-login handler that this is a linking completion flow
        userInitiatedLinkingCompletion: true,
        // Preserve tokenStorageConfig so the completion handler writes tokens to the right cookies
        ...(this.linkingOptions.tokenStorageConfig && { tokenStorageConfig: this.linkingOptions.tokenStorageConfig }),
      },
    };

    // Configure captcha options
    if (this._captchaOptions) {
      loginOptions.authorizationParams = {
        ...loginOptions.authorizationParams,
        disableCaptcha: this._captchaOptions.disableCaptcha,
        captchaType: this._captchaOptions.captchaType,
        auth0LoginConfigToken: this._captchaOptions.auth0LoginConfigToken,
      };
    }

    console.log(`linker: loginWithRedirect redirectUri => ${redirectUri}`);
    console.log(`linker: loginWithRedirect appState.tokenStorageConfig => ${JSON.stringify(loginOptions.appState?.tokenStorageConfig ?? null)}`);
    console.log(`linker: loginWithRedirect => ${JSON.stringify(loginOptions)}`);
    await a0.loginWithRedirect(loginOptions);
    // Note: This will redirect away from current page; code below won't execute
  };

  _getSecondaryUserClaims = async () => {
    const a0 = await this._getIdpAuth0Client();
    return a0.getIdTokenClaims();
  };

  _writeLoginToShipStationMessageCookie = () => {
    Auth0UtilsBrowser.setOption({
      cookieName: Auth0Constants.INFO_MESSAGE,
      value: Auth0Localizer.getLocalizedText('login-to-shipstation', { idpName: this._idpInfo.name }),
      expirySec: 30,
    });
  };

  _writeLinkingRequestResult = (linkingRequestInfo) => {
    // an app that requested linking may emit this cookie before starting the link flow. If present, we will update it with linking status
    // linking status has the following contract:
    // {
    //   success: bool,
    //   idpName: string,
    //   idpId: string,
    //   ssUsername: string,
    //   idpUsername: string,
    //   message: string,
    // }
    if (!Auth0CookieHelper.getCookieValue(Auth0Constants.AUTH0_LINKING_REQUEST_STATE)) {
      console.log('Account linker did not find a request for linking state cookie...');
      return;
    }
    Auth0CookieHelper.setCookieValueWithExpirationInSeconds(Auth0Constants.AUTH0_LINKING_REQUEST_STATE, JSON.stringify(linkingRequestInfo), 180);
  };

  _preLogoutActions(logoutType = Auth0Constants.LOGOUT_TYPE_REDIRECT) {
    super._preLogoutActions(logoutType);
    // Clear linking-specific cookies that need to be removed during linking page logout
    Auth0CookieHelper.deleteCookie(Auth0Constants.AUTH0_IDP_REDIRECT_LOGIN);
    Auth0CookieHelper.deleteCookie(Auth0Constants.AUTH0_IDP_CONTEXT_KEY);
    Auth0CookieHelper.deleteCookie(Auth0Constants.AUTH0_RESTRICTED_ACCOUNT_LINK_KEY);
    Auth0CookieHelper.deleteCookie(Auth0Constants.AUTH0_SOURCE_CONTEXT_KEY);
  }

  _cancelLinkToShipStationAccount = async (logoutType = Auth0Constants.LOGOUT_TYPE_REDIRECT) => {
    this._logout(logoutType);
  };

  _cancelLinkToShipStationAccountFederated = async () => {
    await this._cancelLinkToShipStationAccount(Auth0Constants.LOGOUT_TYPE_FEDERATED_REDIRECT);
  };

  _refreshLoginOnAppDomain = () => {
    // Use shared utility to trigger re-login with linked account (sets cookie and redirects)
    Auth0UtilsBrowser.triggerReLoginWithLinkedAccount(
      this.linkingOptions.idp_id,
      this.linkingOptions.appLoginHandlerUrl,
      this.linkingOptions.redirectUrl || null,
    );
  };

  _startLinkToShipStationAccount = async () => {
    this._enableLinkingConfirmButton(false);
    // Account linking process (redirect-based):
    // 1. Cache the SSO user's JWT in sessionStorage
    // 2. Redirect to SS login (same tab, not popup) with appState.userInitiatedLinkingCompletion=true
    // 3. After SS auth, post-login handler detects the flag and:
    //    a. Retrieves cached SSO JWT from sessionStorage
    //    b. Calls SSAS /user/link with BOTH JWTs (SS user JWT from auth, SSO JWT from cache)
    //    c. SSAS validates both JWTs server-side, then links using M2M credentials
    // 4. Post-login handler triggers re-login with linked identity
    //
    // This redirect-based flow works in Safari, iOS WebViews, and with corporate security policies
    // that block popups.

    const externalAppLinkingRequestResult = {
      success: false,
      idpName: this._idpInfo.name,
      idpId: this._idpInfo.id,
      idpUsername: this.linkingOptions.email,
      ssUsername: undefined,
      message: 'Unknown failure',
    };

    try {
      // Redirect to SS auth - this will navigate away from current page
      // Linking completion happens in the post-login handler after SS auth
      await this._loginToShipStationWithRedirect();

      // Note: Code below won't execute because we redirect
      // The linking result cookie is written by the post-login handler after linking completes
    } catch (e) {
      console.log('Auth0LinkingPageHelper._startLinkToShipStationAccount has failed.', e);

      // Clear any cached credentials on error
      Auth0LinkingPageHelper.clearCachedSsoCredentials();

      if (e.message?.toLowerCase() === 'timeout') {
        this._displayError('linking-flow-timeout');
        this._enableLinkingConfirmButton(false, 'error-timeout');
        externalAppLinkingRequestResult.message = 'timeout';
        return;
      }

      this._handledUnexpectedException(e);
      this._displayError(e, e);
      externalAppLinkingRequestResult.message = Auth0UtilsBrowser.errorToString(e);
      this._enableLinkingConfirmButton(true);
    } finally {
      this._writeLinkingRequestResult(externalAppLinkingRequestResult);
    }
  };

  _applyWhiteLabel = async (whiteLabelOptions) => {
    console.log(`_applyWhiteLabel => ${JSON.stringify(whiteLabelOptions)}`);
    this._whiteLabelHelper = new Auth0WhiteLabelAssetHelper({
      tenantOrDomain: this.linkingOptions.tenant,
      ...whiteLabelOptions,
    });

    await this._whiteLabelHelper.loadCustomStyles();

    document.getElementById('ss-logo').src = this._whiteLabelHelper.brandLogoUrl;
    document.getElementById('ss-icon').src = this._whiteLabelHelper.brandIconUrl;

    this._whiteLabelHelper.setFavIcons();
  };

  _localize = async () => {
    await super._localize();
    if (this.linkingOptions.given_name) {
      Auth0Localizer.setElementLocalizedText('hi-given-name', { givenName: this.linkingOptions.given_name });
    } else {
      Auth0Localizer.setElementLocalizedText('hi-there');
    }
    Auth0Localizer.setElementLocalizedText('idp-link-prompt', { idpName: this.linkingOptions.idp_name, appName: this.linkingOptions.appName });
    Auth0Localizer.setElementLocalizedText('connect-to-idp-button', this.linkingOptions.idp_name);
    Auth0Localizer.setElementLocalizedText('idp-link-cancel');
    Auth0Localizer.setElementLocalizedText('not-you-link', { idpName: this.linkingOptions.idp_name });
  };

  _initShipStationAccountLinkHtml = async () => {
    await this._localize();
    await this._applyWhiteLabel(this._whiteLabelOptions);

    document.getElementById('idp-icon').src = this.linkingOptions.idp_icon;

    // init the IdP "ID Card"
    document.getElementById('id-card-email').innerText = this.linkingOptions.email;
    document.getElementById('id-card-picture').src = this.linkingOptions.picture;

    // add event handlers for start link and cancel link
    document.getElementById('connect-to-idp-button').addEventListener('click', this._startLinkToShipStationAccount);
    document.getElementById('idp-link-cancel').addEventListener('click', this._cancelLinkToShipStationAccount);
    document.getElementById('not-you-link').addEventListener('click', this._cancelLinkToShipStationAccountFederated);

    if (this._idpInfo.supportsFederatedLogout) {
      document.getElementById('idp-link-federated-logout-container').style.display = 'block';
    }

    this._showPage();
  };

  async _onLoad() {
    await super._onLoad();
    // called when render of underlying template is complete

    this.toolbarVisible = true;
    this.actionLinkVisible = true;
    this.actionLinkText = Auth0Localizer.getLocalizedText('toolbar-action-cancel');
    this.actionLinkTextHandler = this._cancelLinkToShipStationAccount;
    await this.setBody(AccountLinkingHtml);
  }

  /**
   * @function
   * @async
   * @description Directs the `Auth0LinkingPageHelper` to start the linking process. A valid SSO Identity Provider auth must have already happened before this page loads.
   */
  async _startUi() {
    await super._startUi();

    this.mgmtApi = new Auth0ManagementApiBrowser({
      tenantOrDomain: this.tenant,
      clientId: this.linkingOptions.clientId,
      authServiceBaseUrl: resolveSsasBaseUrl('auth-stage', 'shipstation.com,sslocal.com', window.location.hostname),
    });
    await this._initShipStationAccountLinkHtml();
  }
}

export { Auth0LinkingPageHelper };
